import { cn } from '@lib/utils/helpers'
import type { ApplyChoiceOptionProps } from '../types/components'

/**
 * Presentational choice card.
 * Content is always a single horizontal row: [radio] [icon] [text].
 * layout="grid" only stretches height for side-by-side cards.
 */
export function ApplyChoiceOption({
  title,
  description,
  selected,
  onSelect,
  icon,
  layout = 'stack',
  name,
  value,
}: ApplyChoiceOptionProps) {
  const isGrid = layout === 'grid'

  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={cn(
        'w-full rounded-lg border-2 p-3 text-left transition-colors sm:px-3.5 sm:py-3',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2',
        selected
          ? 'border-teal-dark bg-white'
          : 'border-gray-200 bg-white hover:border-gray-300',
        isGrid && 'h-full',
      )}
    >
      <span className="flex w-full items-center gap-2.5 sm:gap-3">
        <RadioDot selected={selected} />
        <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-teal-50 text-teal-dark sm:h-9 sm:w-9">
          {icon}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold leading-tight text-teal-dark sm:text-[0.9375rem]">
            {title}
          </span>
          <span className="mt-0.5 block text-xs leading-snug text-gray-500">
            {description}
          </span>
        </span>
      </span>

      <input
        type="radio"
        name={name}
        value={value}
        checked={selected}
        onChange={onSelect}
        className="sr-only"
        tabIndex={-1}
      />
    </button>
  )
}

function RadioDot({ selected }: { selected: boolean }) {
  return (
    <span
      className={cn(
        'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2',
        selected ? 'border-teal-dark' : 'border-gray-300',
      )}
      aria-hidden="true"
    >
      {selected ? <span className="h-2.5 w-2.5 rounded-full bg-teal-dark" /> : null}
    </span>
  )
}
